import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { withAuth } from '@/components/auth-wrapper';
import { DealsTable } from '@/components/deals-list/deals-table';
import { InternalGuard } from '@/components/internal-guard';
import { ChooseLogo } from '@/components/ui/choose-logo';
import { useMixpanelIdentify } from '@/hooks/use-mixpanel-identify';
import { useSmartOfferRedesignState } from '@/hooks/use-smart-offer-redesign';
import { useUser } from '@/hooks/use-user';
import { trackEvent } from '@/lib/mixpanel';
import { Provider, createStore } from 'jotai';

// Keep list state across route unmounts, but discard it when the account changes.
let dealsSession = {
  userId: undefined as string | undefined,
  store: createStore(),
};

export function PublicHomePage() {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = t('page.home.title');
  }, [t]);

  useEffect(() => {
    trackEvent('HOME_VIEWED');
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-background to-muted">
      <div className="text-center">
        <div className="mb-6 flex justify-center">
          <ChooseLogo width={90} height={90} />
        </div>
        <h1
          data-testid="choose-title"
          className="text-4xl font-bold tracking-tight"
        >
          {t('page.home.title')}
        </h1>
      </div>
    </div>
  );
}

const InternalHomePage = withAuth(function InternalHomePage() {
  const user = useUser();
  const userId = user?.providerUser.sub;
  if (dealsSession.userId !== userId) {
    dealsSession = { userId, store: createStore() };
  }
  return (
    <InternalGuard fallback={<PublicHomePage />}>
      <Provider key={userId} store={dealsSession.store}>
        <DealsTable />
      </Provider>
    </InternalGuard>
  );
});

export default function HomePage() {
  useMixpanelIdentify();
  const enabled = useSmartOfferRedesignState();
  if (enabled === null) return null;
  return enabled ? <InternalHomePage /> : <PublicHomePage />;
}
